import { addDays, addMonths, format, parse } from "date-fns";

import { DateFormat } from "constants/date";
import { ReportDistribution } from "constants/reports/distribution";
import { getFormattedNumberValue } from "helpers/string";

import { ChangeLabel } from "../../Breakdown/ChangeValue";
import { getDateRangeLabel } from "../chart-legend";

import { IExtendedColumn } from "./interfaces";
import { getColumnEvenOddClassName } from "./utils";

export function getCellWithCompare(compareKey: string, valueParser: (value: unknown) => number | string) {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, react/display-name, @typescript-eslint/no-explicit-any
  return ({ value, original }: any) => {
    const compareValue = original[compareKey];
    const change = value - compareValue;
    const changeValue = Math.abs(change);
    return (
      <span>
        {valueParser ? valueParser(value) : getFormattedNumberValue(value)}
        {change !== 0 && !isNaN(change) && (
          <ChangeLabel
            value={valueParser ? valueParser(changeValue) : getFormattedNumberValue(changeValue)}
            isPositive={change >= 0}
          />
        )}
      </span>
    );
  };
}

export function getHoursColumns(
  uniqueDateRanges: string[],
  labels: string[],
  isEvenFixedColumnCount: boolean,
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueParser?: (value: any) => any
): IExtendedColumn[] {
  const columns: IExtendedColumn[] = [];
  const dateRanges = uniqueDateRanges.map(dateRangeString => dateRangeString.split("_")) as [string, string][];

  for (let i = 0; i < labels.length; i += 1) {
    const evenClassName = getColumnEvenOddClassName(i, !isEvenFixedColumnCount ? 1 : 0);
    const columnClassName = `data-column ${evenClassName}`;
    const vsClassNames = `vs ${evenClassName}`;
    const label = labels[i];

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newColumns: any[] = dateRanges.map((dateRange, dateRangeIndex) => {
      const accessor = `${label}_${dateRange.join("_")}`;

      const comparisonIndex = dateRangeIndex ? dateRangeIndex - 1 : 0;
      const compareAccessor = `${label}_${dateRanges[comparisonIndex].join("_")}`;

      return {
        Header:
          dateRanges.length > 1 ? (
            <span>
              {label}
              <small>{getDateRangeLabel(...dateRange)}</small>
            </span>
          ) : (
            label
          ),
        accessor,
        width: 160,
        className: columnClassName,
        headerClassName: columnClassName,
        // @ts-expect-error TS(2345): Argument of type '((value: any) => any) | undefine... Remove this comment to see the full error message
        Cell: getCellWithCompare(compareAccessor, valueParser),
      };
    });

    if (dateRanges.length > 1) {
      newColumns.splice(1, 0, {
        Header: "vs",
        width: 60,
        sortable: false,
        className: vsClassNames,
        headerClassName: vsClassNames,
      });
    }

    columns.push(...newColumns);
  }

  return columns;
}

export function getDateRangeColumns(
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  series: any[],
  uniqueDateRanges: string[],
  distribution: ReportDistribution,
  isEvenFixedColumnCount: boolean,
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueParser?: (value: any) => any
): IExtendedColumn[] {
  const dateLongestPeriod = Math.max(...series.map(s => s.data.length));
  const columns: IExtendedColumn[] = [];
  const addPeriodFnc = distribution === ReportDistribution.Day ? addDays : addMonths;

  const displayDateFormat =
    distribution === ReportDistribution.Day ? DateFormat.ChartDailyLabel : DateFormat.ChartMonthlyLabel;

  const dateRanges = uniqueDateRanges.map(dateRangeString => dateRangeString.split("_"));

  for (let i = 0; i < dateLongestPeriod; i += 1) {
    const evenClassName = getColumnEvenOddClassName(i, !isEvenFixedColumnCount ? 1 : 0);
    const columnClassName = ["data-column", evenClassName].join(" ");
    const vsClassNames = ["vs", evenClassName].join(" ");

    /* eslint-disable no-loop-func */
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newColumns: any[] = dateRanges.map(([dateFrom, dateTo], dateRangeIndex) => {
      const date = addPeriodFnc(dateFrom, i);
      const formatterDate = format(date, DateFormat.HtmlInputDate);

      const header = parse(dateTo) < date ? "-" : format(date, displayDateFormat);

      const compareDate = addPeriodFnc(dateRanges[dateRangeIndex ? dateRangeIndex - 1 : 0][0], i);
      const formattedCompareDate = format(compareDate, DateFormat.HtmlInputDate);

      return {
        Header: header,
        accessor: `${formatterDate}_${dateRangeIndex}`,
        width: 130,
        className: columnClassName,
        headerClassName: columnClassName,
        // @ts-expect-error TS(2345): Argument of type '((value: any) => any) | undefine... Remove this comment to see the full error message
        Cell: getCellWithCompare(`${formattedCompareDate}_${dateRangeIndex - 1}`, valueParser),
      };
    });
    /* eslint-enable no-loop-func */

    if (dateRanges.length > 1) {
      newColumns.splice(1, 0, {
        Header: "vs",
        className: vsClassNames,
        headerClassName: vsClassNames,
        width: 20,
        sortable: false,
      });
    }

    columns.push(...newColumns);
  }

  return columns;
}
